import React from 'react';
import { cn } from '../utils/cn';

export type BadgeTone =
  | 'neutral'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'info';

export type BadgeVariant = 'soft' | 'solid' | 'outline' | 'dot';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  variant?: BadgeVariant;
  /** When provided, badge renders as a small dot */
  dot?: boolean;
}

const softTone: Record<BadgeTone, string> = {
  neutral: 'bg-surface-variant text-surface-variant-foreground',
  primary: 'bg-primary-container text-primary-container-foreground',
  secondary: 'bg-secondary-container text-secondary-container-foreground',
  tertiary: 'bg-tertiary-container text-tertiary-container-foreground',
  success: 'bg-success-container text-success-container-foreground',
  warning: 'bg-warning-container text-warning-container-foreground',
  destructive: 'bg-error-container text-error-container-foreground',
  info: 'bg-info-container text-info-container-foreground',
};

const solidTone: Record<BadgeTone, string> = {
  neutral: 'bg-surface-foreground text-background',
  primary: 'bg-primary text-primary-foreground',
  secondary: 'bg-secondary text-secondary-foreground',
  tertiary: 'bg-tertiary text-tertiary-foreground',
  success: 'bg-success text-success-foreground',
  warning: 'bg-warning text-warning-foreground',
  destructive: 'bg-destructive text-destructive-foreground',
  info: 'bg-info text-info-foreground',
};

const outlineTone: Record<BadgeTone, string> = {
  neutral: 'border border-outline text-foreground',
  primary: 'border border-primary text-primary',
  secondary: 'border border-secondary text-secondary-foreground',
  tertiary: 'border border-tertiary text-tertiary-foreground',
  success: 'border border-success text-success',
  warning: 'border border-warning text-warning-foreground',
  destructive: 'border border-destructive text-destructive',
  info: 'border border-info text-info',
};

export const Badge: React.FC<BadgeProps> = ({ tone = 'neutral', variant = 'soft', dot, className, children, ...props }) => {
  if (dot) {
    return (
      <span
        className={cn(
          'inline-block h-2 w-2 rounded-full',
          tone === 'primary' && 'bg-primary',
          tone === 'secondary' && 'bg-secondary',
          tone === 'tertiary' && 'bg-tertiary',
          tone === 'success' && 'bg-success',
          tone === 'warning' && 'bg-warning',
          tone === 'destructive' && 'bg-destructive',
          tone === 'info' && 'bg-info',
          tone === 'neutral' && 'bg-outline',
          className,
        )}
        aria-hidden
        {...props}
      />
    );
  }
  const styles =
    variant === 'solid' ? solidTone[tone] : variant === 'outline' ? outlineTone[tone] : softTone[tone];
  return (
    <span
      className={cn(
        'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-2 text-label-sm font-medium tabular-nums',
        styles,
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
};
